import { Flex } from '@common/components/Flex';
import * as React from 'react';
import { DatasheetComponentTextStyle } from '@common/dto/datasheet/component-settings/text-style';
import { DatasheetFieldComponentTextAlign } from '@common/dto/datasheet/field-component/text-align';

import './datasheet-item-label.styles.scss';

export type DatasheetItemLabelProps = {
  text: string;
  img: string;
  withImg?: boolean;
  withText?: boolean;
  textCustomization?: {
    fontStyles?: Array<DatasheetComponentTextStyle>;
    fontSize?: number;
    fontFamily?: string;
    color?: number;
    align?: DatasheetFieldComponentTextAlign;
  };
  inline?: boolean;
  reference?: React.RefObject<HTMLDivElement>;
  absolutePosition?: boolean;
  alignmentStyle?: React.CSSProperties;
  positionOffset?: { top?: number; left?: number; bottom?: number; right?: number };
};

export const DatasheetItemLabel: React.FC<DatasheetItemLabelProps> = (props) => {
  const { img, text, withImg, withText, textCustomization, inline, reference, absolutePosition, alignmentStyle, positionOffset } = props;

  const justifyMap: Record<DatasheetFieldComponentTextAlign, React.CSSProperties['justifyContent']> = {
    [DatasheetFieldComponentTextAlign.center]: 'center',
    [DatasheetFieldComponentTextAlign.left]: 'flex-start',
    [DatasheetFieldComponentTextAlign.right]: 'flex-end',
  };

  // Convert BGR number to RGB hex string
  const getColor = React.useMemo(() => {
    // Default to black if color is not set or is invalid
    if (textCustomization?.color === undefined || textCustomization?.color === null) {
      return '#000000';
    }
    // Extract BGR bytes from number and output as RGB hex
    // Number is stored as BGR: bits 0-7 = Blue, 8-15 = Green, 16-23 = Red
    const colorNum = typeof textCustomization.color === 'number' ? textCustomization.color : 0;
    const blue = (colorNum & 0xFF);
    const green = ((colorNum >> 8) & 0xFF);
    const red = ((colorNum >> 16) & 0xFF);
    // Output as RGB hex string
    const hex = [
      red.toString(16).padStart(2, '0'),
      green.toString(16).padStart(2, '0'),
      blue.toString(16).padStart(2, '0')
    ].join('');
    return `#${hex}`;
  }, [textCustomization?.color]);

  // Build style object for absolute positioning
  const flexStyle: React.CSSProperties = React.useMemo(() => {
    if (absolutePosition) {
      return {
        position: 'absolute',
        ...positionOffset,
        ...alignmentStyle,
      };
    }

    return {};
  }, [absolutePosition, positionOffset, alignmentStyle]);

  // For absolute positioning, use inline styles for alignment; otherwise use Flex's justify prop
  const justifyValue = !absolutePosition && textCustomization?.align ? justifyMap[textCustomization.align] : undefined;

  return (
    <Flex 
      inline={inline}
      reference={reference}
      className="DatasheetsDatasheetItemLabel" 
      alignItems="center"
      justify={justifyValue}
      style={flexStyle}
    >
      {withImg && <img className="DatasheetsDatasheetItemLabel__img" src={img} alt={text} />}
      {withText && (
        <span 
          style={{
            fontSize: textCustomization?.fontSize,
            fontFamily: textCustomization?.fontFamily,
            color: getColor,
            fontStyle: textCustomization?.fontStyles?.includes(DatasheetComponentTextStyle.italic) ? 'italic' : undefined,
            fontWeight: textCustomization?.fontStyles?.includes(DatasheetComponentTextStyle.bold) ? 'bold' : undefined,
            textDecoration: textCustomization?.fontStyles?.includes(DatasheetComponentTextStyle.underline) ? 'underline' : 'none',
          }}
          className="DatasheetsDatasheetItemLabel__text"
        >
          {text}
        </span>
      )}
    </Flex>
  );
};